import React, { useEffect, useState } from 'react';
import {
  Box,
  Switch,
  Tooltip,
  IconButton,
  FormControlLabel,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Typography, PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import type { TableAction, TableColumn } from '@/components/common/DataTable/types';
import DataTable from '@/components/common/DataTable';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
} from '@mui/material';
import { RoleFormDrawer } from '../../components';
import { rolesService } from '@/superadmin/services/rolesService';
import { toast } from 'react-hot-toast';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

// Types for Role rows
export interface RoleRow extends Record<string, unknown> {
  id: string;
  name: string;
  description: string;
  users: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Start with empty list, populate from API
const initialRoles: RoleRow[] = [];

const RolesPage: React.FC = () => {
  const [rows, setRows] = useState<RoleRow[]>(initialRoles);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<RoleRow | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState<string | undefined>(undefined);

  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const resp = await rolesService.list(currentPage, pageSize, appliedSearch);
        console.log('Roles API Response:', resp);
        const apiRoles = resp?.results || [];
        console.log('API Roles:', apiRoles);
        const mapped: RoleRow[] = apiRoles.map(r => ({
          id: r.uuid,
          name: r.role_name,
          description: '',
          users: 0,
          is_active: r.is_active,
          created_at: r.created_at,
          updated_at: r.updated_at,
        }));
        setRows(mapped);
        setTotalCount(resp?.count || 0);
      } catch (e) {
        console.warn('Failed to load roles:', e);
        toast.error('Failed to load roles');
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, [currentPage, pageSize, appliedSearch]);

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/roles');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Roles', active: true },
        ];

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Filter handlers
  const handleSearch = () => {
    setAppliedSearch(searchTerm.trim() || undefined);
    setCurrentPage(1); // Reset to first page when applying search
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAppliedSearch(undefined);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Toggle active status
  const handleToggleActive = async (roleId: string, currentStatus: boolean) => {
    try {
      setToggling(roleId);
      await rolesService.toggleActive(roleId, !currentStatus);

      // Update local state
      setRows(prevRows =>
        prevRows.map(row => (row.id === roleId ? { ...row, is_active: !currentStatus } : row))
      );

      toast.success(`Role ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.warn('Failed to toggle role status:', error);
      toast.error('Failed to update role status');
    } finally {
      setToggling(null);
    }
  };

  // Columns
  const columns: TableColumn<RoleRow>[] = [
    {
      field: 'name',
      headerName: 'Role Name',
      minWidth: 200,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" fontWeight="medium">
          {String(params.value)}
        </Typography>
      ),
    },
    // {
    //   field: 'is_active',
    //   headerName: 'Status',
    //   minWidth: 120,
    //   sortable: true,
    //   renderCell: params => (
    //     <Box display="flex" alignItems="center" gap={1}>
    //       <Chip
    //         label={params.value ? 'Active' : 'Inactive'}
    //         color={params.value ? 'success' : 'default'}
    //         size="small"
    //         variant="outlined"
    //       />
    //       <FormControlLabel
    //         control={
    //           <Switch
    //             checked={Boolean(params.value)}
    //             onChange={() => handleToggleActive(params.row.id, Boolean(params.value))}
    //             disabled={toggling === params.row.id}
    //             size="small"
    //           />
    //         }
    //         label=""
    //         sx={{ margin: 0 }}
    //       />
    //     </Box>
    //   ),
    // },
    {
      field: 'is_active',
      headerName: 'Status',
      minWidth: 120,
      sortable: true,
      renderCell: params => {
        const canUpdateStatus = hasPermission('ADMIN_ROLE_STATUS_UPDATE');

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={params.value ? 'Active' : 'Inactive'}
              color={params.value ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />

            {canUpdateStatus && (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(params.value)}
                    onChange={() => handleToggleActive(params.row.id, Boolean(params.value))}
                    size="small"
                  />
                }
                label=""
                sx={{ margin: 0 }}
              />
            )}
          </Box>
        );
      },
    },

    {
      field: 'created_at',
      headerName: 'Created At',
      minWidth: 180,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">{new Date(String(params.value)).toLocaleString()}</Typography>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      minWidth: 180,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">{new Date(String(params.value)).toLocaleString()}</Typography>
      ),
    },
  ];

  // Header actions
  const headerActions: PageHeaderAction[] = [
    hasPermission('ADMIN_ROLE_ADD') && {
      label: 'New Role',
      icon: <AddIcon sx={{ fontSize: 16 }} />,
      onClick: () => {
        setEditing(null);
        setDrawerOpen(true);
      },
      variant: 'contained',
      size: 'medium',
    },
  ].filter(Boolean) as PageHeaderAction[];

  const actions: TableAction<RoleRow>[] = [
    hasPermission('ADMIN_ROLE_DELETE') && {
      label: 'Delete',
      icon: <DeleteIcon />,
      color: 'error',
      onClick: ({ row }) => {
        setToDelete(row);
        setConfirmOpen(true);
      },
      danger: true,
    },
  ].filter(Boolean) as TableAction<RoleRow>[];

  const handleSubmitRole = async (payload: { id?: string; name: string }) => {
    try {
      setLoading(true);
      if (editing) {
        await rolesService.update(editing.id, { role_name: payload.name });
        toast.success('Role updated');
      } else {
        await rolesService.create({ role_name: payload.name });
        toast.success('Role created');
      }

      // Refresh list
      const resp = await rolesService.list(currentPage, pageSize, appliedSearch);
      const mapped: RoleRow[] = (resp?.results || []).map(r => ({
        id: r.uuid,
        name: r.role_name,
        description: '',
        users: 0,
        is_active: r.is_active,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      setRows(mapped);
      setTotalCount(resp?.count || 0);
      setDrawerOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error('Failed to save role');
      console.warn('Save role failed:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Roles"
        subtitle={`Manage system roles (${totalCount} ${totalCount === 1 ? 'role' : 'roles'})`}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      {/* Filter Controls */}
      <Box
        mb={3}
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box display="flex" gap={2} alignItems="center">
          {/* Search Filter */}
          <TextField
            variant="outlined"
            type="search"
            placeholder="Search roles"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          />

          {/* Search Button */}
          {/* <MuiButton
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{ ml: 1 }}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </MuiButton> */}
        </Box>

        {/* Clear Filters Button */}
        <Box>
          <MuiButton
            sx={{ mr: 8 }}
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
          >
            Clear Filters
          </MuiButton>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => {
                handleSearch();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table (no summary cards per requirement) */}
      {hasPermission('ADMIN_ROLE_LIST_VIEW') ? (
        <DataTable
          data={rows}
          columns={columns}
          actions={actions}
          keyField="id"
          title="Roles"
          showTitle={false}
          loading={loading}
          pagination={true}
          rowsPerPageOptions={[5, 10, 15, 25, 50]}
          defaultRowsPerPage={15}
          page={currentPage - 1}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={page => handlePageChange(page + 1)}
          onRowsPerPageChange={handlePageSizeChange}
          sortable={true}
          stickyHeader={true}
          maxHeight={600}
          striped={true}
          emptyState={
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No roles found.
              </Typography>
            </Box>
          }
        />
      ) : (
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view roles.
        </Typography>
      )}

      <RoleFormDrawer
        open={drawerOpen}
        initialValues={editing ? { id: editing.id, name: editing.name } : null}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmitRole}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{toDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)}>Cancel</MuiButton>
          <MuiButton
            color="error"
            onClick={async () => {
              if (!toDelete) return;
              try {
                setLoading(true);
                await rolesService.delete(toDelete.id);
                const resp = await rolesService.list(currentPage, pageSize, appliedSearch);
                const mapped: RoleRow[] = (resp?.results || []).map(r => ({
                  id: r.uuid,
                  name: r.role_name,
                  description: '',
                  users: 0,
                  is_active: r.is_active,
                  created_at: r.created_at,
                  updated_at: r.updated_at,
                }));
                setRows(mapped);
                setTotalCount(resp?.count || 0);
                toast.success('Role deleted');
              } catch (e) {
                toast.error('Failed to delete role');
                console.warn('Delete role failed:', e);
              } finally {
                setLoading(false);
                setConfirmOpen(false);
                setToDelete(null);
              }
            }}
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPage;
